import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Clock, ExternalLink, Eye, EyeOff, Landmark, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { BANK_ACCOUNT_KINDS, formatCurrency, type BankAccount, type BankAccountKind, type BankConnection } from '@tally/shared';
import { banksApi } from '@/services/api';
import { monthLabel } from '@/utils/time';
import { cn } from '@/utils/cn';

const BRIDGE_URL = 'https://beta-bridge.simplefin.org/';

const KIND_LABELS: Record<BankAccountKind, string> = {
  checking: 'Checking',
  savings: 'Savings',
  credit: 'Credit card',
  investment: 'Investment',
  loan: 'Loan',
  other: 'Other',
};

/** Banks read through SimpleFIN Bridge: connect with a setup token, then balances and history per account. */
export function BankConnections() {
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);

  const { data } = useQuery({
    queryKey: ['banks'],
    queryFn: () => banksApi.list(),
    // Poll only while a sync runs (the first one reads up to two years and takes a minute).
    refetchInterval: (query) => (query.state.data?.data.some((c) => c.syncing) ? 4000 : false),
  });
  const connections = data?.data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['banks'] });

  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-950">
            <Landmark className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="font-semibold">Bank accounts</h2>
            <p className="text-xs text-muted-foreground">Balances and transactions, read-only through SimpleFIN Bridge</p>
          </div>
        </div>
        {!connecting && (
          <button
            onClick={() => setConnecting(true)}
            className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent"
          >
            <Plus className="h-3.5 w-3.5" /> Connect banks
          </button>
        )}
      </div>

      {connecting && <ConnectForm onDone={() => { setConnecting(false); refresh(); }} onCancel={() => setConnecting(false)} />}

      {connections.length > 0 && <Totals connections={connections} />}

      <div className="space-y-3">
        {connections.map((c) => <ConnectionCard key={c.id} connection={c} onChange={refresh} />)}
        {connections.length === 0 && !connecting && (
          <p className="text-sm text-muted-foreground">
            Connect your banks and cards to see balances and every posted transaction, including ones no email mentions.
          </p>
        )}
      </div>
    </section>
  );
}

function ConnectForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [token, setToken] = useState('');
  const connect = useMutation({ mutationFn: () => banksApi.connectSimplefin(token), onSuccess: onDone });

  return (
    <div className="mb-4 rounded-lg border border-border bg-muted/30 p-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm">
        <li>
          In{' '}
          <a href={BRIDGE_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
            SimpleFIN Bridge <ExternalLink className="h-3 w-3" />
          </a>
          , link the banks and cards you want Tally to see.
        </li>
        <li>Create a new app connection there and copy its setup token. Give each app its own token.</li>
        <li>Paste the token here. It works once; Tally keeps the access it unlocks encrypted.</li>
      </ol>
      <textarea
        value={token}
        onChange={(e) => setToken(e.target.value)}
        placeholder="Setup token"
        rows={3}
        spellCheck={false}
        className="mt-3 w-full rounded-lg border border-border bg-background p-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary"
      />
      {connect.error && <p className="mt-2 text-xs text-destructive">{(connect.error as Error).message}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <button onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent">Cancel</button>
        <button
          onClick={() => connect.mutate()}
          disabled={!token.trim() || connect.isPending}
          className="flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {connect.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Connect
        </button>
      </div>
    </div>
  );
}

/** Cash in checking and savings, and what the cards owe, over visible USD accounts. */
function Totals({ connections }: { connections: BankConnection[] }) {
  const accounts = connections.flatMap((c) => c.accounts).filter((a) => !a.hidden && a.currency === 'USD' && a.balance !== null);
  const sum = (kinds: BankAccountKind[]) => accounts.filter((a) => kinds.includes(a.kind)).reduce((s, a) => s + (a.balance ?? 0), 0);
  const cash = sum(['checking', 'savings']);
  const owed = -sum(['credit']);
  if (accounts.length === 0) return null;
  return (
    <div className="mb-4 grid grid-cols-3 gap-2 text-center">
      <Stat label="Cash" value={formatCurrency(cash, 'USD')} />
      <Stat label="Cards owe" value={formatCurrency(owed, 'USD')} />
      <Stat label="Cash − cards" value={formatCurrency(cash - owed, 'USD')} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/40 px-2 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold tabular-nums sm:text-base">{value}</p>
    </div>
  );
}

function ConnectionCard({ connection, onChange }: { connection: BankConnection; onChange: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const sync = useMutation({ mutationFn: () => banksApi.sync(connection.id), onSuccess: onChange });
  const remove = useMutation({ mutationFn: () => banksApi.remove(connection.id), onSuccess: onChange });
  const problem = connection.status !== 'active' || connection.lastError;

  return (
    <div className={cn('rounded-lg border p-3', connection.status === 'active' ? 'border-border' : 'border-destructive/40 bg-destructive/5')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-muted-foreground">
          {connection.syncing ? (
            <span className="flex items-center gap-1 text-primary">
              <Loader2 className="h-3 w-3 animate-spin" />
              {connection.historySynced ? 'Syncing…' : 'Reading up to two years of history…'}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> SimpleFIN · {connection.lastSyncAt ? `synced ${new Date(connection.lastSyncAt).toLocaleString()}` : 'not synced yet'} · syncs daily
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => sync.mutate()}
            disabled={connection.syncing || sync.isPending || connection.status === 'revoked'}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
            title="Sync now"
          >
            <RefreshCw className={cn('h-4 w-4', (connection.syncing || sync.isPending) && 'animate-spin')} />
          </button>
          <button
            onClick={() => setConfirming(true)}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title="Remove"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {sync.error && <p className="mt-2 text-xs text-muted-foreground">{(sync.error as Error).message}</p>}
      {problem && connection.lastError && (
        <div className="mt-2 flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {connection.lastError}
        </div>
      )}

      {confirming && (
        <div className="mt-2 rounded-md border border-destructive/40 p-3 text-xs">
          <p>
            Removes these accounts and every transaction read through them from Tally. Also disable Tally&apos;s
            connection in SimpleFIN Bridge, since only you can turn that access off there.
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <button onClick={() => setConfirming(false)} className="rounded-lg px-3 py-1 text-muted-foreground hover:bg-accent">Cancel</button>
            <button
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              className="rounded-lg bg-destructive px-3 py-1 font-medium text-destructive-foreground disabled:opacity-50"
            >
              Remove
            </button>
          </div>
          {remove.error && <p className="mt-2 text-destructive">{(remove.error as Error).message}</p>}
        </div>
      )}

      <div className="mt-3 divide-y divide-border">
        {connection.accounts.map((a) => <AccountLine key={a.id} account={a} onChange={onChange} />)}
        {connection.accounts.length === 0 && !connection.syncing && (
          <p className="py-2 text-xs text-muted-foreground">No accounts yet. Link banks in SimpleFIN Bridge, then sync.</p>
        )}
      </div>
    </div>
  );
}

function AccountLine({ account, onChange }: { account: BankAccount; onChange: () => void }) {
  const update = useMutation({
    mutationFn: (body: { kind?: BankAccountKind; hidden?: boolean }) => banksApi.updateAccount(account.id, body),
    onSuccess: onChange,
  });
  const history = account.firstTransactionDate
    ? `${account.transactionCount.toLocaleString()} transactions since ${monthLabel(account.firstTransactionDate)}`
    : 'No transactions yet';

  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 py-2', account.hidden && 'opacity-50')}>
      <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
        <p className="truncate text-sm font-medium">{account.name}</p>
        <p className="truncate text-xs text-muted-foreground">{[account.institution, history].filter(Boolean).join(' · ')}</p>
      </div>
      <select
        value={account.kind}
        onChange={(e) => update.mutate({ kind: e.target.value as BankAccountKind })}
        className="rounded-md border border-border bg-background px-2 py-1 text-xs"
        aria-label={`Kind of ${account.name}`}
      >
        {BANK_ACCOUNT_KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
      </select>
      <p className="ml-auto text-right text-sm font-semibold tabular-nums sm:ml-0 sm:w-28">
        {account.balance === null ? '—' : formatCurrency(account.balance, account.currency)}
      </p>
      <button
        onClick={() => update.mutate({ hidden: !account.hidden })}
        className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        title={account.hidden ? 'Show this account' : 'Hide this account'}
      >
        {account.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}
